import { ValueSelectorProps } from './props';
import styles from './styles.module.scss';
import { Button } from '@starsoft/common/components';
import { CoinType } from '@starsoft/common/models';
import { useMemo } from 'react';
import { WalletService } from '@starsoft/common/services';
import useDepositSearchParams from '../../../../../../../../hooks/deposit/useDepositSearchParams';

/**
 * ValueSelector Component
 * This component is responsible for rendering a list of suggested amounts as buttons for selection.
 * It displays either the suggested amounts or skeleton loaders based on the loading state.
 *
 * @param {ValueSelectorProps} props - The properties for the component.
 * @param {number} props.currentValue - The currently selected value.
 * @param {string} props.coin - The type of coin for the transaction.
 * @param {Function} props.onSelectValue - Callback function to handle the selection of a value.
 * @param {boolean} props.isLoading - Flag indicating if the data is still loading.
 * @param {Array} props.suggestedAmounts - List of suggested amounts for selection.
 *
 * @returns {JSX.Element | null} The rendered component displaying the suggested amounts or null if conditions are not met.
 *
 * @example
 * // Usage example:
 * const currentValue = 100;
 * const coin = 'USD';
 * const onSelectValue = (value) => function(value);
 * const isLoading = false;
 * const suggestedAmounts = [{ id: '1', amount: 100 }, { id: '2', amount: 200 }];
 * <ValueSelector
 *   currentValue={currentValue}
 *   coin={coin}
 *   onSelectValue={onSelectValue}
 *   isLoading={isLoading}
 *   suggestedAmounts={suggestedAmounts}
 * />
 */
export default function ValueSelector({
  currentValue,
  coin,
  onSelectValue,
  isLoading,
  suggestedAmounts,
}: ValueSelectorProps) {
  // Retrieve the coin type from the deposit search parameters
  const { coinType } = useDepositSearchParams();

  // Memoize the list of amount buttons to optimize rendering
  const Amounts: JSX.Element[] = useMemo(
    () =>
      suggestedAmounts?.map(suggestedAmount => (
        <Button
          type="button"
          isSecondary={
            currentValue?.toNumber() != suggestedAmount?.amount?.toNumber()
          }
          key={`suggested_amount_${suggestedAmount?.id}`}
          onClick={() => onSelectValue(suggestedAmount?.amount)}
          isSmall
          className={styles.container__button}
        >
          {WalletService.maskCurrency({
            amount: suggestedAmount?.amount?.toNumber(),
            coin,
          })}
        </Button>
      )),
    [suggestedAmounts, coin, currentValue, onSelectValue],
  );

  // Memoize the skeleton loaders for the loading state
  const Skeletons: JSX.Element[] = useMemo(
    () =>
      Array.from({ length: 6 }).map((_, index: number) => (
        <Button
          isSkeleton
          key={`value-selector-options-${index}`}
          skeletonDefault
        />
      )),
    [],
  );

  // Return null if the coin type is Crypto or if there are no suggested amounts
  if (
    coinType == CoinType.Crypto ||
    !suggestedAmounts ||
    suggestedAmounts?.length === 0
  ) {
    return null;
  }

  // Render the container with either the skeletons or the amount buttons
  return (
    <div className={styles.container}>{isLoading ? Skeletons : Amounts}</div>
  );
}
